import { ReportOptions } from "models/component-print-models";
import { ComponentConfigDefault, HighlightMapped } from "models/dataset-models";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { CSSProperties, useEffect, useId } from "react";
import { formatDateTime } from "utils/time-utils";
import { TIME_UNITS_FROM_DAYS } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { DashboardComponentPropsBase } from "../../pages/dashboard/components/interfaces";
import { TitleOverrideAndDescriptionTemplate } from "../../templates/DataTableCellTemplates";
import { formatNumberWithGaps, getValueColors } from "../product-template-table/utils/utils";
import DatasetChartButtonWrapper, { TableTypes } from "./DatasetChartButtonWrapper";
import { adjustTimeToTz, TimezoneDisplayLocation } from "../charts/utils";

interface DatasetTableTransposedProps extends DashboardComponentPropsBase<ReportOptions> {
  data: Array<any>;
  config: ComponentConfigDefault;
  currentPage: number;
  highlightRanges: Array<HighlightMapped>;
  stepUnit?: string;

  formatNumbers: boolean;

  getPageDates: (page: number) => Array<string>;
}

const DatasetTableTransposed = ({
  data,
  currentPage,
  getPageDates,
  config,
  highlightRanges,
  reportMode,
  formatNumbers,
  stepUnit,
}: DatasetTableTransposedProps) => {
  const tableId = useId();
  useEffect(() => {
    if (config.hideDateColumn) {
      const table = document.getElementById(tableId);
      if (table) {
        const dateColumn = table.querySelector("thead");
        if (dateColumn) {
          dateColumn.style.display = "none";
        }
      }
    }
  }, [config]);

  const setColors = (date: string, obj: any): CSSProperties | undefined => {
    const objValue = obj[date];

    const id = highlightRanges.find(e => e.identifier === obj.identifier);

    const [color, bgColor] = getValueColors(objValue, {
      highlightRanges: id?.highlightRanges,
      defaultHighlightColor: id?.defaultHighlight,
    });
    return { color, backgroundColor: bgColor };
  };

  const columnHeaderBody = (row: any) => {
    // Maybe for the report mode (or all report component config) there should be provider context
    return (
      <div style={{ padding: "6px" }} className="flex gap-2">
        <TitleOverrideAndDescriptionTemplate
          data={{
            description: row.description,
            title: row.title,
            overriddenTitle: row.overriddenTitle,
            unit: row.unit,
            identifier: row.identifier,
          }}
        />
        {reportMode ?? (
          <DatasetChartButtonWrapper
            identifier={row.identifier}
            tableType={TableTypes.TRANSPOSED}
          />
        )}
      </div>
    );
  };

  const renderColumnBody = (e: any, date: string) => {
    const returnValue = formatNumbers ? formatNumberWithGaps(e[date]) : e[date];
    return (
      <div className="column-with-padding-for-color" style={setColors(date, e)}>
        {e[date] != null ? returnValue : "-"}
      </div>
    );
  };
  const matchingUnit = TIME_UNITS_FROM_DAYS.find(unit => unit.enumValue === stepUnit);
  const valueColumnTemplate = (date: string) => {
    return (
      <Column
        header={formatDateTime(adjustTimeToTz(new Date(date), TimezoneDisplayLocation.CLIENT), matchingUnit ? "yyyy-MM-dd" : "yyyy-MM-dd HH:mm")}
        className="column-dataset-table-transposed"
        key={date}
        field={date}
        body={e => renderColumnBody(e, date)}
        style={{ width: 0, whiteSpace: "nowrap" }}
      />
    );
  };

  return (
    <div className="data-table-wrapper transposed-table">
      <DataTable value={data} size="small" showGridlines id={tableId}>
        <Column
          className={
            config.hideDateColumn
              ? "column-into-header column-dataset-table-transposed"
              : "column-dataset-table-transposed"
          }
          header=""
          field="title"
          body={columnHeaderBody}
          style={{ whiteSpace: "nowrap" }}
        />
        {getPageDates(currentPage).map(date => valueColumnTemplate(date))}
      </DataTable>
    </div>
  );
};

export default DatasetTableTransposed;
